import Badge from "@/src/components/atom/Badge";
import Typo from "@/src/components/atom/Typo";
import colorSet from "@/src/styles/color";
import type {
  PlanDto,
  PlanVersionDto,
  SubscriptionDto,
} from "@/src/store/apis/subscription/interface";
import {
  calculateAnnualDiscountRate,
  calculateMonthlyFromAnnual,
  hasValidAnnualPricing,
} from "@/src/utils/subscription";
import { CheckOutlined } from "@ant-design/icons";
import { ReactComponent as OpenIcon } from "@/src/assets/icons/icon-open.svg";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export type PaymentCycle = "MONTHLY" | "ANNUALLY";

export interface PlanSelectionGridProps {
  plans: PlanDto[];
  paymentCycle: PaymentCycle;
  onPaymentCycleChange: (cycle: PaymentCycle) => void;
  onPlanSelect: (plan: PlanDto, version: PlanVersionDto) => void;
  onEnterpriseContact: () => void;
  isLoading?: boolean;
  currentSubscription?: SubscriptionDto;
}

const isNotFreeTrialPlan = (plan: PlanDto): boolean => {
  return !plan.planCode?.toLowerCase().includes("free trial");
};

const isNormalPlan = (plan: PlanDto): boolean => {
  return plan.planType === "NORMAL";
};

const hasActiveVersion = (plan: PlanDto): boolean => {
  return plan.planVersionList?.some((v) => v.isVersionActivated) || false;
};

const isValidSubscriptionPlan = (plan: PlanDto): boolean => {
  return (
    isNotFreeTrialPlan(plan) && isNormalPlan(plan) && hasActiveVersion(plan)
  );
};

const sortPlansByIdAscending = (a: PlanDto, b: PlanDto): number => {
  return (a.id || 0) - (b.id || 0);
};

/**
 * PlanSelectionGrid - 구독 플랜 선택을 위한 공통 컴포넌트
 *
 * @description
 * 신규 구독과 플랜 변경 모두에서 사용할 수 있는 플랜 선택 그리드 컴포넌트.
 * 결제 주기 토글, 플랜 카드들, Enterprise 플랜 카드를 포함.
 *
 * @features
 * - 월간/연간 결제 주기 토글
 * - 플랜 필터링 및 정렬 (활성화된 Normal 플랜만)
 * - 가격 계산 및 할인율 표시
 * - 현재 구독 플랜 표시 (선택적)
 * - Enterprise 플랜 문의 카드
 * - 로딩 상태 처리
 */
const PlanSelectionGrid: React.FC<PlanSelectionGridProps> = ({
  plans,
  paymentCycle,
  onPaymentCycleChange,
  onPlanSelect,
  onEnterpriseContact,
  isLoading = false,
  currentSubscription,
}) => {
  const { t } = useTranslation([
    "subscriptionPlanSignup",
    "subscriptionManagement",
    "common",
  ]);

  const freePlanLabel = t("subscriptionManagement:subscriptionStatus.free");

  const formatPriceLabel = (price: number) => {
    if (price <= 0) {
      return freePlanLabel;
    }

    return `${price.toLocaleString()} USD`;
  };

  const normalPlans = useMemo(() => {
    if (!plans || plans.length === 0) {
      return [];
    }

    const filtered = plans
      .filter(isValidSubscriptionPlan)
      .sort(sortPlansByIdAscending);

    return filtered;
  }, [plans]);

  return (
    <>
      <ToggleContainer>
        <ToggleWrapper>
          <ToggleButton
            $isActive={paymentCycle === "MONTHLY"}
            onClick={() => onPaymentCycleChange("MONTHLY")}
          >
            {t("subscriptionPlanSignup:monthlySubscription")}
          </ToggleButton>
          <ToggleButton
            $isActive={paymentCycle === "ANNUALLY"}
            onClick={() => onPaymentCycleChange("ANNUALLY")}
          >
            {t("subscriptionPlanSignup:annuallySubscription")}
          </ToggleButton>
        </ToggleWrapper>
      </ToggleContainer>

      <PlansGrid>
        {isLoading ? (
          <>
            {[1, 2, 3].map((i) => (
              <PlanCardSkeleton key={i} />
            ))}
          </>
        ) : (
          <>
            {normalPlans.map((plan) => {
              const activeVersions =
                plan.planVersionList?.filter((v) => v.isVersionActivated) || [];
              const currentVersion =
                activeVersions.find((v) => v.paymentCycle === paymentCycle) ||
                activeVersions[0];

              if (!currentVersion) {
                return null;
              }

              const isCurrentPlan = plan.id === currentSubscription?.plan?.id;

              const monthlyPrice = Number(currentVersion.monthlyFee || 0);
              const annualPrice = Number(currentVersion.annualFee || 0);

              const monthlyFromAnnual = calculateMonthlyFromAnnual(annualPrice);

              const discountRate =
                paymentCycle === "ANNUALLY" &&
                hasValidAnnualPricing(annualPrice)
                  ? calculateAnnualDiscountRate(monthlyPrice, annualPrice)
                  : 0;

              const hasDiscount = discountRate > 0;

              return (
                <PlanCard key={plan.id} $isCurrent={isCurrentPlan}>
                  <PlanHeader>
                    <PlanNameWrapper>
                      <PlanName>{plan.name || plan.planCode}</PlanName>
                      {isCurrentPlan && (
                        <Badge
                          text={t("subscriptionPlanSignup:badgeCurrentPlan")}
                          badgeColor="systemLime5"
                          color="systemLime1"
                          badgeSize="S"
                        />
                      )}
                    </PlanNameWrapper>
                    <PriceWrapper>
                      {paymentCycle === "MONTHLY" ? (
                        <PriceContainer>
                          <PriceAmount>
                            {formatPriceLabel(monthlyPrice)}
                          </PriceAmount>
                          <PriceUnit>
                            /{t("subscriptionPlanSignup:month")}
                          </PriceUnit>
                        </PriceContainer>
                      ) : (
                        <>
                          <PriceContainer>
                            <PriceAmount>
                              {formatPriceLabel(annualPrice)}
                            </PriceAmount>
                            <PriceUnit>
                              /
                              {t("subscriptionPlanSignup:annuallySubscription")}
                            </PriceUnit>
                          </PriceContainer>
                          {hasValidAnnualPricing(annualPrice) && (
                            <MonthlyPriceInfo>
                              <MonthlyPriceText>
                                {Math.round(monthlyFromAnnual).toLocaleString()}{" "}
                                USD /{t("subscriptionPlanSignup:month")}
                              </MonthlyPriceText>
                              {hasDiscount && (
                                <DiscountBadge>
                                  <Badge
                                    text={`${discountRate}% ${t("subscriptionPlanSignup:discount")}`}
                                    badgeColor="systemBlue6"
                                    color="systemBlue2"
                                    badgeSize="S"
                                  />
                                </DiscountBadge>
                              )}
                            </MonthlyPriceInfo>
                          )}
                        </>
                      )}
                    </PriceWrapper>
                  </PlanHeader>

                  <PlanFeatures>
                    <FeatureItem>
                      <FeatureLabel>
                        {t("subscriptionPlanSignup:usedAccount")}
                      </FeatureLabel>
                      <FeatureValue>
                        {currentVersion.numberOfMember || 0}
                      </FeatureValue>
                    </FeatureItem>
                    <FeatureItem>
                      <FeatureLabel>
                        {t("subscriptionPlanSignup:loadingCode")}
                      </FeatureLabel>
                      <FeatureValue>
                        {currentVersion.numberOfLoadingCode || 0}
                      </FeatureValue>
                    </FeatureItem>
                  </PlanFeatures>

                  <SelectButton
                    $isCurrent={isCurrentPlan}
                    disabled={isCurrentPlan}
                    onClick={() => onPlanSelect(plan, currentVersion)}
                  >
                    {isCurrentPlan && <StyledCheckIcon />}
                    {isCurrentPlan
                      ? t("subscriptionPlanSignup:cardCurrentPlan")
                      : t("subscriptionManagement:subscription.button.planChoice")}
                  </SelectButton>
                </PlanCard>
              );
            })}

            <PlanCard $isCurrent={false}>
              <PlanHeader>
                <PlanName>
                  {t(
                    "subscriptionManagement:subscription.table.enterprisePlanType",
                  )}
                </PlanName>
                <PriceContainer>
                  <PriceAmount>{t("common:custom")}</PriceAmount>
                </PriceContainer>
              </PlanHeader>

              <EnterpriseContent>
                <EnterpriseDescription>
                  {t("subscriptionPlanSignup:enterpriseDescription")}
                </EnterpriseDescription>
              </EnterpriseContent>

              <ContactButton onClick={onEnterpriseContact}>
                {t("enterpriseSignup:button.contactSalesTeam")}
                <StyledOpenIcon />
              </ContactButton>
            </PlanCard>
          </>
        )}
      </PlansGrid>
    </>
  );
};

export default memo(PlanSelectionGrid);

const StyledCheckIcon = styled(CheckOutlined)`
  color: ${colorSet.blue4};
`;

const StyledOpenIcon = styled(OpenIcon)`
  width: 16px;
  height: 16px;
`;

const ToggleContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ToggleWrapper = styled.div`
  display: inline-flex;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  border-radius: 8px;
`;

const ToggleButton = styled.button<{ $isActive: boolean }>`
  padding: 8px 24px;
  background: white;
  color: ${({ $isActive }) => ($isActive ? colorSet.blue4 : colorSet.gray4)};
  font-size: 16px;
  font-weight: ${({ $isActive }) => ($isActive ? 500 : 400)};
  line-height: 24px;
  border: 1px solid
    ${({ $isActive }) => ($isActive ? colorSet.blue4 : colorSet.gray8)};
  cursor: pointer;
  min-width: 160px;
  position: relative;

  &:first-child {
    border-radius: 8px 0 0 8px;
    border-right: ${({ $isActive }) =>
      $isActive ? `1px solid ${colorSet.blue4}` : "none"};
  }

  &:last-child {
    border-radius: 0 8px 8px 0;
    border-left: ${({ $isActive }) =>
      $isActive ? `1px solid ${colorSet.blue4}` : "none"};
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(280px, 420px));
  gap: 20px;
  width: 100%;
  max-width: calc(4 * 420px + 3 * 20px + 40px);
  justify-content: center;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 1780px) {
    grid-template-columns: repeat(3, minmax(280px, 420px));
    max-width: calc(3 * 420px + 2 * 20px + 40px);
  }

  @media (max-width: 1340px) {
    grid-template-columns: repeat(2, minmax(280px, 420px));
    max-width: calc(2 * 420px + 20px + 40px);
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(280px, 420px);
    max-width: 460px;
  }
`;

const PlanCard = styled.div<{ $isCurrent: boolean }>`
  padding: 28px 24px 24px;
  background: white;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  outline: 2px solid
    ${({ $isCurrent }) => ($isCurrent ? colorSet.blue4 : colorSet.gray10)};
  outline-offset: -2px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 24px;
  min-height: 340px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2);
  }
`;

const PlanHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PlanNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PlanName = styled(Typo)`
  color: ${colorSet.blue4};
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  word-wrap: break-word;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const PriceAmount = styled(Typo)`
  color: ${colorSet.gray2};
  font-size: 28px;
  font-weight: 700;
  line-height: 38px;
  word-wrap: break-word;
`;

const PriceUnit = styled(Typo)`
  color: ${colorSet.gray6};
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  word-wrap: break-word;
`;

const MonthlyPriceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const MonthlyPriceText = styled(Typo)`
  color: ${colorSet.gray6};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  word-wrap: break-word;
`;

const DiscountBadge = styled.div`
  display: inline-flex;
`;

const PlanFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureLabel = styled(Typo)`
  color: ${colorSet.gray5};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  word-wrap: break-word;
`;

const FeatureValue = styled(Typo)`
  color: ${colorSet.gray2};
  font-size: 19px;
  font-weight: 500;
  line-height: 26px;
  word-wrap: break-word;
`;

const SelectButton = styled.button<{ $isCurrent: boolean }>`
  width: 100%;
  height: 40px;
  padding: 10px 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: ${({ $isCurrent }) =>
    $isCurrent ? colorSet.gray10 : colorSet.indigo};
  color: ${({ $isCurrent }) => ($isCurrent ? colorSet.gray2 : "white")};
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  word-wrap: break-word;
  border: none;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  overflow: hidden;
  cursor: ${({ $isCurrent }) => ($isCurrent ? "not-allowed" : "pointer")};

  &:hover {
    background: ${({ $isCurrent }) =>
      $isCurrent ? colorSet.gray10 : colorSet.indigo2};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const EnterpriseContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const EnterpriseDescription = styled(Typo)`
  color: ${colorSet.gray2};
  font-size: 17px;
  font-weight: 400;
  line-height: 24px;
  white-space: pre-line;
`;

const ContactButton = styled.button`
  width: 100%;
  height: 40px;
  padding: 10px 12px;
  background: ${colorSet.blue10};
  color: ${colorSet.indigo};
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  border: 0.5px solid rgba(23, 98, 195, 0.2);
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;

  &:hover {
    background: ${colorSet.blue11};
  }
`;

const PlanCardSkeleton = styled.div`
  padding: 28px 24px 24px;
  background: ${colorSet.gray11};
  border-radius: 16px;
  border: 1px solid ${colorSet.gray10};
  height: 300px;
  position: relative;
  overflow: hidden;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
  }
`;
